import { useContext, useEffect, useRef } from "react";
import { Context } from "../EditorContext";
import Col from "../Col/Col";
import RowTool from "./RowTool";
import image from "../Image/image-json-tpl";
import formText from "../FormText/form-text-json-tpl";
import formPhone from "../FormPhone/form-phone-json-tpl";
import formOption from "../FormOption/form-option-json-tpl";
import formRadioGrid from "../FormRadioGrid/form-radio-grid-json-tpl";
import formMultiOption from "../FormMultiOption/form-multi-option-json-tpl";
import formDropdown from "../FormDropdown/form-dropdown-json-tpl";
import formTextarea from "../FormTextarea/form-textarea-json-tpl";
import formRating from "../FormRating/form-rating-json-tpl";
import formEmail from "../FormEmail/form-email-json-tpl";
import formDate from "../FormDate/form-date-json-tpl";
import formNumber from "../FormNumber/form-number-json-tpl";
import formUrl from "../FormUrl/form-url-json-tpl";
import formTerms from "../FormTerms/form-terms-json-tpl";
import formSubmitButton from "../FormSubmitButton/form-submit-button-json-tpl";
import heading from "../Heading/heading-tpl";
import text from "../Text/text-json-tpl";
import divider from "../Divider/divider-json-tpl";
import htmlBlock from "../Html/html-block";
import contentBlock from "../WechatContent/wechat-content";
import wechatQRCode from "../WechatQRCode/wechat-qrcode";
import wechatFollow from "../WechatFollow/wechat-follow";
import wechatHeader from "../WechatHeader/wechat-header";
import button from "../Button/button-json-tpl";
import wechatTextImage from "../WechatTextImage/wechat-content-image";
import wechatTrackableQRCode from "../WechatTrackableQRCode/wechat-trackable-qrcode";
import WechatSubscribeMessage from "../WechatSubscribeMessage/WechatSubscribeMessage";
import WechatLink from "../WeChatLink/WechatLink";
import col_12 from "../Col/col-12";
import col_3_9 from "../Col/col-3-9";
import col_4_8 from "../Col/col-4-8";
import col_6_6 from "../Col/col-6-6";
import col_8_4 from "../Col/col-8-4";
import col_4_4_4 from "../Col/col-4-4-4";
import col_3_3_6 from "../Col/col-3-3-6";
import col_3_6_3 from "../Col/col-3-6-3";
import col_6_3_3 from "../Col/col-6-3-3";
import col_3_3_3_3 from "../Col/col-3-3-3-3";
import col_2_2_2_2_2_2 from "../Col/col-2-2-2-2-2-2";
import { useUpdate } from "ahooks";
import { useRowContext } from "./RowContext";
import ColContext from "../Col/ColContext";
import { toRGBA } from "../parser/utils";
import map from "../Map/map-json-tpl";
import countdownJsonTpl from "../Countdown/countdown-json-tpl";
import couponJsonTpl from "../Coupon/coupon-json-tpl";
import formThankyouJsonTpl from "../FormThankyou/form-thankyou-json-tpl";

// initialize value of component config
export const JSON_TPL_MAPPING = {
  heading: heading,
  text: text,
  image: image,
  divider: divider,
  button: button,
  html: htmlBlock,
  countdown: countdownJsonTpl,
  coupon:couponJsonTpl,
  map: map,
  "wechat-content": contentBlock,
  "wechat-qrcode": wechatQRCode,
  "wechat-follow": wechatFollow,
  "wechat-header": wechatHeader,
  "wechat-link": WechatLink,
  "wechat-content-image": wechatTextImage,
  "wechat-subscribe-message": WechatSubscribeMessage,
  "wechat-trackable-qrcode": wechatTrackableQRCode,
  "form-text": formText,
  "form-email": formEmail,
  "form-date": formDate,
  "form-number": formNumber,
  "form-url": formUrl,
  "form-terms": formTerms,
  "form-textarea": formTextarea,
  "form-phone": formPhone,
  "form-option": formOption,
  "form-radio-grid": formRadioGrid,
  "form-multi-option": formMultiOption,
  "form-dropdown": formDropdown,
  "form-rating": formRating,
  "form-submit-button": formSubmitButton,
  "form-coupon": formText,
  "form-thankyou": formThankyouJsonTpl,
  "col-12": col_12,
  "col-3-9": col_3_9,
  "col-4-8": col_4_8,
  "col-6-6": col_6_6,
  "col-8-4": col_8_4,
  "col-4-4-4": col_4_4_4,
  "col-3-3-6": col_3_3_6,
  "col-3-6-3": col_3_6_3,
  "col-6-3-3": col_6_3_3,
  "col-3-3-3-3": col_3_3_3_3,
  "col-2-2-2-2-2-2": col_2_2_2_2_2_2,
};

export const getRowBackgroundImageConfig = ({
  enableRowBackgroundImage,
  rowBackgroundImage,
  fitToBackground,
  repeatBackgroundImage,
  centerBackgroundImage,
}) => {
  if (!enableRowBackgroundImage) return {};
  return {
    backgroundImage: `url(${rowBackgroundImage})`,
    backgroundPosition: centerBackgroundImage ? "center top" : "left top",
    backgroundRepeat: repeatBackgroundImage ? "repeat" : "no-repeat",
    backgroundSize: fitToBackground ? "cover" : "auto",
  };
};

export default ({ node, rowIndex }) => {
  const { shareRefs, state } = useContext(Context);
  const { rowShareRefs } = useRowContext();
  const rowContentRef = useRef();

  const updateCurrentRow = useUpdate();

  useEffect(() => {
    rowShareRefs.updateCurrentRow = updateCurrentRow;
  }, []);

  const {
    paddingTop,
    paddingBottom,
    paddingRight,
    paddingLeft,
    applyBackgroundTo = "row",
    rowBackgroundColor,
    applyImageTo = "row",
    gutter,
  } = node.data;

  const root = state.root;

  let contentWidth = "";
  if (root.data.sideImage) {
    contentWidth = "100%";
  } else if (root.data.contentAreaWidthMode === "percentage") {
    contentWidth = `${root.data.contentAreaWidthPercentage || 100}%`;
  } else {
    contentWidth = root.data.contentAreaWidth;
  }

  let backgroundColor = "transparent";
  if (applyBackgroundTo === "content") {
    backgroundColor = toRGBA(rowBackgroundColor || shareRefs.tree.root.data.contentAreaBackgroundColor);
  } else {
    backgroundColor = toRGBA(shareRefs.tree.root.data.contentAreaBackgroundColor);
  }


  return (
    <RowTool node={node} rowIndex={rowIndex}>
      <div
        className="rip-mm-row-content"
        ref={rowContentRef}
        style={{
          paddingTop,
          paddingBottom,
          paddingRight,
          paddingLeft,
          maxWidth: contentWidth,
          backgroundColor,
          ...(applyImageTo === "content"
            ? getRowBackgroundImageConfig(node.data)
            : {}),
        }}
      >
        {node.children.map((node, colIndex) => {
          return (
            <ColContext key={node.id}>
              <Col node={node} gutter={gutter / 2} colIndex={colIndex} />
            </ColContext>
          );
        })}
      </div>
    </RowTool>
  );
};
